// @em/blocks — the SHARE block's rules (ROADMAP A2b #55).
//
// Where an event link goes when a guest presses a network button, as PURE functions
// with no React in sight. They live apart from `ShareBlockView` for the same reason
// `homeOverview.ts` and `sectionNav.ts` do: a guard can RUN them, and every way this
// can be wrong is silent.
//
// ⚠⚠ THE FAILURE MODE HERE IS A BUTTON THAT WORKS AND SHARES NOTHING. Each of these
// is an ordinary outbound link — it opens the network, the composer appears, the guest
// posts it — and the event link is absent, doubled, or literally the word "undefined".
// Nobody reports it, because the person who lost the link is the one who never arrived.
// So:
//   • `wa.me` and `mailto:` take ONE free-text field and NO url parameter — the link
//     must be built INTO the text or the message carries no event at all;
//   • X takes `url` and `text` SEPARATELY — putting the link in both posts it twice;
//   • Facebook has ignored a caller-supplied quote/description since 2017, so it gets
//     the url alone and the card comes from the page's own Open Graph tags;
//   • every value is encoded exactly once (`URLSearchParams` already encodes — a
//     hand-rolled `encodeURIComponent` on top of it double-encodes, and a `?` or `&`
//     in an event title then turns the shared link into a 404).
//
// ⚠ NO BRAND COLOURS, DELIBERATELY. The obvious design is a row of Facebook-blue /
// WhatsApp-green chips, which is seven raw hexes in a package whose one colour rule is
// "T.* tokens, never raw hex" — and they are unreadable on a dark event theme, which is
// exactly the class of bug this site has shipped three times. The brand ICON says which
// network it is; the event's own theme says what the page looks like.

/** The networks the share block can offer. */
export type ShareNetworkId = 'facebook' | 'x' | 'linkedin' | 'whatsapp' | 'telegram' | 'reddit' | 'email';

/** What is being shared, before validation. */
export interface ShareSubject {
  /** The event's public URL. Must be absolute http(s) — see `normalizeShareSubject`. */
  url?: string | null;
  /** The event title — a network's subject / headline. */
  title?: string | null;
  /** The host's own sharing message, else one built from the title. */
  text?: string | null;
}

/** A subject that has passed validation — `url` is guaranteed absolute http(s). */
export interface NormalizedShareSubject {
  url: string;
  title: string;
  text: string;
}

export interface ShareNetwork {
  id: ShareNetworkId;
  /** Button label. */
  label: string;
  /**
   * AppIcon name.
   *
   * ⚠ AN ICON NAME THE GENERATED REGISTRY LACKS RENDERS A SILENT QUESTION MARK — no error,
   * no warning, just a wrong glyph on a public page. The view reads these DYNAMICALLY
   * (`iconName={net.icon}`), so what saves us is the generator's `icon:\s*"..."` object
   * pattern picking them up out of THIS table — i.e. the literal quoted string below is
   * load-bearing. Compute one of these names and it vanishes from the registry while the
   * code still compiles. ▶ `npm run check-share` asserts every name here is present in
   * BOTH generated registries, so it fails however the table is written.
   */
  icon: string;
  /** Builds the outbound share URL for this network. */
  build: (subject: NormalizedShareSubject) => string;
}

/** The sharing message used when the host has not written one. */
export const defaultShareText = (title: string): string => (title ? `Check out ${title}` : 'Check this out');

/**
 * Validate + fill in a share subject. Returns null when there is nothing shareable.
 *
 * ⚠ THE URL CHECK IS THE POINT. Everything downstream string-concatenates it, so a
 * missing or relative url does not throw — it produces `https://wa.me/?text=undefined`
 * and a button that cheerfully shares the word "undefined". An empty ctx slice (the
 * editor preview, or a page rendered before the origin is threaded through) must make
 * the block say it has no link yet, never offer a broken one.
 */
export const normalizeShareSubject = (subject: ShareSubject): NormalizedShareSubject | null => {
  const url = (subject.url ?? '').trim();

  if (!/^https?:\/\/\S+$/i.test(url)) {
    return null;
  }

  const title = (subject.title ?? '').trim();
  const text = (subject.text ?? '').trim() || defaultShareText(title);

  return { url, title, text };
};

/**
 * The network table. `build` returns a complete, ready-to-open URL.
 *
 * ⚠ `URLSearchParams.toString()` percent-encodes every value once. Do not wrap a value
 * in `encodeURIComponent` before handing it over.
 */
export const SHARE_NETWORKS: ShareNetwork[] = [
  {
    id: 'facebook',
    label: 'Facebook',
    icon: 'BrandFacebook',
    // ⚠ URL ONLY. Facebook dropped caller-supplied `quote` / `description` in 2017 and
    // silently ignores them; the card a friend sees is built from the event page's own
    // Open Graph tags (lib/seo.ts), which is why those matter more than anything here.
    build: ({ url }) => `https://www.facebook.com/sharer/sharer.php?${new URLSearchParams({ u: url }).toString()}`,
  },
  {
    id: 'x',
    label: 'X',
    icon: 'BrandX',
    // ⚠ `url` and `text` are SEPARATE — X appends the link itself. Including the link in
    // `text` too posts it twice and eats the character budget.
    build: ({ url, text }) => `https://twitter.com/intent/tweet?${new URLSearchParams({ url, text }).toString()}`,
  },
  {
    id: 'linkedin',
    label: 'LinkedIn',
    icon: 'BrandLinkedin',
    // ⚠ URL only — LinkedIn reads the page's OG tags and ignores caller-supplied copy.
    build: ({ url }) => `https://www.linkedin.com/sharing/share-offsite/?${new URLSearchParams({ url }).toString()}`,
  },
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    icon: 'BrandWhatsapp',
    // ⚠⚠ ONE FREE-TEXT FIELD, NO `url` PARAMETER. `wa.me/?text=` IS the whole message, so
    // the link has to be inside it. Pass only the title here and the guest sends a
    // recommendation with nothing to click.
    build: ({ url, text }) => `https://wa.me/?${new URLSearchParams({ text: `${text} ${url}` }).toString()}`,
  },
  {
    id: 'telegram',
    label: 'Telegram',
    icon: 'BrandTelegram',
    // Telegram DOES take both, and renders its own preview from the url.
    build: ({ url, text }) => `https://t.me/share/url?${new URLSearchParams({ url, text }).toString()}`,
  },
  {
    id: 'reddit',
    label: 'Reddit',
    icon: 'BrandReddit',
    // A Reddit submission is TITLED, not captioned — it takes the event title, not the
    // sharing message (`text` would become a self-post body and drop the link entirely).
    build: ({ url, title, text }) =>
      `https://www.reddit.com/submit?${new URLSearchParams({ url, title: title || text }).toString()}`,
  },
  {
    id: 'email',
    label: 'Email',
    icon: 'Mail',
    // ⚠ SAME TRAP AS WHATSAPP — `mailto:` has no url field, so the link lives in the body.
    // ⚠⚠ AND NOT `URLSearchParams` HERE. It writes a space as `+` — the web-form rule, right for every https target
    // above — but `mailto:` follows RFC 6068, where `+` is a literal plus: mail apps showed "Check+out+Spring+Gala"
    // in the subject and body (owner, 2026-09-29). `encodeURIComponent` writes `%20`, which every client decodes.
    build: ({ url, title, text }) =>
      `mailto:?subject=${encodeURIComponent(title || text)}&body=${encodeURIComponent(`${text}\n\n${url}`)}`,
  },
];

/** Every network id, in display order. */
export const SHARE_NETWORK_IDS: ShareNetworkId[] = SHARE_NETWORKS.map((n) => n.id);

/**
 * Which networks a block shows.
 *
 * ⚠ AN ABSENT SETTING MEANS "ALL"; AN EMPTY LIST MEANS "NONE". A page saved before this
 * block existed, and a brand-new block the host has not opened yet, both have no
 * `shareNetworks` key at all — falling through to "none" there would render an empty
 * block with no explanation. But a host who deliberately unticks every network has
 * chosen an empty list and must get what they chose (the view then offers copy / QR
 * alone). `undefined` and `[]` are NOT the same value here: `??` preserves the
 * difference and `||` would collapse it.
 */
export const selectedShareNetworks = (selected?: ShareNetworkId[] | string[] | null): ShareNetwork[] => {
  if (selected === undefined || selected === null) {
    return SHARE_NETWORKS;
  }

  const wanted = new Set(selected.map((s) => `${s}`));

  return SHARE_NETWORKS.filter((n) => wanted.has(n.id));
};

/** Build every enabled network's link for one subject. Empty when there is nothing to share. */
export const buildShareLinks = (
  subject: ShareSubject,
  selected?: ShareNetworkId[] | string[] | null,
): { network: ShareNetwork; href: string }[] => {
  const normalized = normalizeShareSubject(subject);

  if (!normalized) {
    return [];
  }

  return selectedShareNetworks(selected).map((network) => ({ network, href: network.build(normalized) }));
};
